// T038-T044: BatchUploadProgress - Visual progress feedback for batch uploads
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { BatchUploadState } from '@/types/upload';
import { Button, Text } from '@/components/atoms';

export interface BatchUploadProgressProps {
  batchState: BatchUploadState;
  onRetry: (id: string) => void;
  onRemove: (id: string) => void;
  onClearCompleted: () => void;
}

export function BatchUploadProgress({
  batchState,
  onRetry,
  onRemove,
  onClearCompleted
}: BatchUploadProgressProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [lastAnnouncedProgress, setLastAnnouncedProgress] = useState(0);

  // T044: Screen reader announcements for progress milestones
  useEffect(() => {
    const progress = batchState.overallProgress;

    // Announce at 25%, 50%, 75%, and 100%
    const milestones = [25, 50, 75, 100];
    const nextMilestone = milestones.find(m => m > lastAnnouncedProgress && progress >= m);

    if (nextMilestone) {
      announceToScreenReader(
        nextMilestone === 100
          ? `Upload complete. ${batchState.completedCount} of ${batchState.totalCount} files uploaded successfully.`
          : `Upload ${nextMilestone}% complete.`
      );
      setLastAnnouncedProgress(nextMilestone);
    }
  }, [batchState.overallProgress, batchState.completedCount, batchState.totalCount, lastAnnouncedProgress]);

  // Reset announcement tracker when new batch starts
  useEffect(() => {
    if (batchState.totalCount === 0) {
      setLastAnnouncedProgress(0);
    }
  }, [batchState.totalCount]);

  // Don't render if no files in queue
  if (batchState.totalCount === 0) {
    return null;
  }

  const content = (
    <div
      className="fixed bottom-4 right-4 w-96 bg-surface-1 border border-border rounded-lg shadow-lg z-50"
      role="status"
      aria-live="polite"
      aria-label="Upload progress"
    >
      {/* T039: Collapsed header */}
      <div
        className="p-4 cursor-pointer hover:bg-surface-2 rounded-t-lg transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
        role="button"
        aria-expanded={isExpanded}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsExpanded(!isExpanded);
          }
        }}
      >
        {batchState.isComplete ? (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-success text-xl" aria-hidden="true">✓</span>
              <Text className="font-semibold">
                Upload complete ({batchState.completedCount}/{batchState.totalCount})
              </Text>
            </div>
            {batchState.hasFailures && (
              <span className="text-danger text-sm">
                {batchState.failedCount} failed
              </span>
            )}
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-between mb-2">
              <Text className="font-semibold">
                Uploading {batchState.completedCount + batchState.failedCount} of {batchState.totalCount}...
              </Text>
              <Text variant="muted" className="text-sm">
                {batchState.overallProgress}%
              </Text>
            </div>
            {/* Progress bar */}
            <div className="w-full h-2 bg-surface-3 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary transition-all duration-300"
                style={{ width: `${batchState.overallProgress}%` }}
                aria-hidden="true"
              />
            </div>
          </div>
        )}
        <div className="text-xs text-muted mt-1 text-right">
          {isExpanded ? '▼ Collapse' : '▶ Expand'}
        </div>
      </div>

      {/* T040: Expanded file list */}
      {isExpanded && (
        <div className="max-h-96 overflow-y-auto border-t border-border">
          {batchState.files.map((file) => (
            <div
              key={file.id}
              className="p-3 border-b border-border last:border-b-0 hover:bg-surface-2"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <Text className="text-sm truncate" title={file.filename}>
                    {file.filename}
                  </Text>
                  <Text variant="muted" className="text-xs">
                    {formatFileSize(file.size)}
                  </Text>
                </div>

                {/* Success state */}
                {file.state === 'success' && (
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-success" aria-label="Upload successful">✓</span>
                    <button
                      onClick={() => onRemove(file.id)}
                      className="text-muted hover:text-foreground text-xs"
                      aria-label={`Remove ${file.filename}`}
                    >
                      ✕
                    </button>
                  </div>
                )}

                {/* Uploading state */}
                {file.state === 'uploading' && (
                  <div className="flex-shrink-0 w-16">
                    <div className="w-full h-1.5 bg-surface-3 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary transition-all"
                        style={{ width: `${file.progress}%` }}
                      />
                    </div>
                    <Text variant="muted" className="text-xs text-right">
                      {file.progress}%
                    </Text>
                  </div>
                )}

                {/* Pending state */}
                {file.state === 'pending' && (
                  <Text variant="muted" className="text-xs flex-shrink-0">
                    Queued...
                  </Text>
                )}

                {/* T041: Error state with retry */}
                {file.state === 'error' && (
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-danger" aria-label="Upload failed">✗</span>
                    <Button
                      variant="link"
                      onClick={() => onRetry(file.id)}
                      className="text-xs px-2 py-1"
                      aria-label={`Retry upload for ${file.filename}`}
                    >
                      Retry
                    </Button>
                  </div>
                )}
              </div>

              {/* Error message */}
              {file.state === 'error' && file.error && (
                <Text className="text-xs text-danger mt-1">{file.error}</Text>
              )}
            </div>
          ))}
        </div>
      )}

      {/* T042: Actions */}
      {batchState.isComplete && (
        <div className="p-3 border-t border-border">
          <Button
            variant="secondary"
            onClick={onClearCompleted}
            className="w-full text-sm"
          >
            Dismiss
          </Button>
        </div>
      )}
    </div>
  );

  // T043: Portal rendering for fixed positioning
  return createPortal(content, document.body);
}

// Helper function for screen reader announcements
function announceToScreenReader(message: string) {
  const announcement = document.createElement('div');
  announcement.setAttribute('role', 'status');
  announcement.setAttribute('aria-live', 'polite');
  announcement.setAttribute('aria-atomic', 'true');
  announcement.className = 'sr-only'; // Screen reader only (needs to be in CSS)
  announcement.textContent = message;

  document.body.appendChild(announcement);

  // Remove after announcement is read
  setTimeout(() => {
    // Check if node still exists before removing (prevents test cleanup errors)
    if (announcement.parentNode) {
      document.body.removeChild(announcement);
    }
  }, 1000);
}

// Helper function to format file sizes
function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`;
}
